import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { haptic } from '../utils/haptic';
import { fireConfetti } from '../utils/confetti';
import { playSound } from '../utils/sound';
import { CoinShower } from './CoinShower';
import { Sixy } from './Sixy';
import { HoloCard, HoloCoin } from './TourSpinners';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { trackEvent } from '../utils/analytics';
import { Z } from '../utils/zIndex';

// SixyFace inline antigo removido — agora usa <Sixy form="robot" /> unificado
// (vide src/components/Sixy.tsx + src/utils/sixyTheme.ts)

/**
 * Guided Tour — passeio apresentado pela Sixy.
 *
 * Arquitetura:
 *  - Um único container motion (avatar + bubble + pointer juntos)
 *  - Container muda de posição via animate { top, left } — tween easeOut suave
 *  - Avatar fica encaixado no canto superior da bubble (peek out), nunca
 *    se separa
 *  - Pointer aponta sempre pro CENTRO HORIZONTAL do target, não pro centro
 *    da bubble (corrige caso a bubble foi shiftada pra caber no viewport)
 *  - Altura da bubble medida via ref pra posicionar acima do target sem
 *    chute de altura
 */

/** Texto que aparece letra por letra — Sixy "fala". Speed em ms/char.
 *  Quando `text` muda, reinicia. Caret pisca no fim enquanto digita. */
const TypingText: React.FC<{ text: string; speed?: number }> = ({ text, speed = 28 }) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    if (!text) return;
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) window.clearInterval(id);
    }, speed);
    return () => window.clearInterval(id);
  }, [text, speed]);
  const isDone = displayed.length === text.length;
  return (
    <>
      {displayed}
      {!isDone && (
        <motion.span
          aria-hidden="true"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.7, repeat: Infinity }}
          className="inline-block ml-0.5 -mb-0.5 h-4 w-[2px] bg-blue-300 align-middle"
        />
      )}
    </>
  );
};

interface TourStep {
  id: string;
  title: string;
  speech: string;
  target?: string;
  route?: string;
  placement?: 'top' | 'bottom' | 'center';
  /** Cor accent do step — tinta backdrop + ring spotlight + dots + CTA. */
  accent?: 'blue' | 'pink' | 'cyan' | 'emerald' | 'amber' | 'violet';
  /** Emoji que aparece num balãozinho ao lado da Sixy — personalidade por step. */
  emoji?: string;
  /** Mood da Sixy nesse step. Default 'happy'. Muda cor + FX + body language. */
  sixyMood?: 'happy' | 'thinking' | 'sad' | 'confused' | 'excited' | 'error';
}

interface AccentColors {
  ring: string;
  ringGlow: string;
  dot: string;
  dotGlow: string;
  backdrop: string; // radial overlay subtil
  ctaGradient: string; // tailwind gradient
  textHighlight: string;
}

const ACCENTS: Record<NonNullable<TourStep['accent']>, AccentColors> = {
  blue: {
    ring: 'rgba(96, 165, 250, 0.7)',
    ringGlow: 'rgba(96, 165, 250, 0.4)',
    dot: 'bg-blue-400',
    dotGlow: 'shadow-[0_0_14px_rgba(96,165,250,0.9)]',
    backdrop: 'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.08), transparent 60%)',
    ctaGradient: 'from-blue-500 via-cyan-500 to-pink-500',
    textHighlight: 'text-blue-300/80',
  },
  pink: {
    ring: 'rgba(236, 72, 153, 0.7)',
    ringGlow: 'rgba(236, 72, 153, 0.4)',
    dot: 'bg-pink-400',
    dotGlow: 'shadow-[0_0_14px_rgba(244,114,182,0.9)]',
    backdrop: 'radial-gradient(circle at 50% 50%, rgba(236,72,153,0.08), transparent 60%)',
    ctaGradient: 'from-pink-500 via-fuchsia-500 to-blue-500',
    textHighlight: 'text-pink-300/80',
  },
  cyan: {
    ring: 'rgba(34, 211, 238, 0.7)',
    ringGlow: 'rgba(34, 211, 238, 0.4)',
    dot: 'bg-cyan-400',
    dotGlow: 'shadow-[0_0_14px_rgba(34,211,238,0.9)]',
    backdrop: 'radial-gradient(circle at 50% 50%, rgba(34,211,238,0.08), transparent 60%)',
    ctaGradient: 'from-cyan-500 via-blue-500 to-violet-500',
    textHighlight: 'text-cyan-300/80',
  },
  emerald: {
    ring: 'rgba(52, 211, 153, 0.7)',
    ringGlow: 'rgba(52, 211, 153, 0.4)',
    dot: 'bg-emerald-400',
    dotGlow: 'shadow-[0_0_14px_rgba(52,211,153,0.9)]',
    backdrop: 'radial-gradient(circle at 50% 50%, rgba(16,185,129,0.08), transparent 60%)',
    ctaGradient: 'from-emerald-500 via-teal-500 to-blue-500',
    textHighlight: 'text-emerald-300/80',
  },
  amber: {
    ring: 'rgba(251, 191, 36, 0.7)',
    ringGlow: 'rgba(251, 191, 36, 0.4)',
    dot: 'bg-amber-400',
    dotGlow: 'shadow-[0_0_14px_rgba(251,191,36,0.9)]',
    backdrop: 'radial-gradient(circle at 50% 50%, rgba(251,191,36,0.08), transparent 60%)',
    ctaGradient: 'from-amber-500 via-pink-500 to-violet-500',
    textHighlight: 'text-amber-300/80',
  },
  violet: {
    ring: 'rgba(167, 139, 250, 0.7)',
    ringGlow: 'rgba(167, 139, 250, 0.4)',
    dot: 'bg-violet-400',
    dotGlow: 'shadow-[0_0_14px_rgba(167,139,250,0.9)]',
    backdrop: 'radial-gradient(circle at 50% 50%, rgba(139,92,246,0.08), transparent 60%)',
    ctaGradient: 'from-violet-500 via-pink-500 to-amber-500',
    textHighlight: 'text-violet-300/80',
  },
};

/** Monta steps dinamicamente baseado no perfil do aluno. */
function buildSteps(opts: { firstName: string; checkinCount: number }): TourStep[] {
  const { firstName, checkinCount } = opts;
  const isNew = checkinCount === 0;
  const greet = firstName ? `Olá, ${firstName}` : 'Olá!';
  return [
    {
      id: 'welcome',
      title: greet,
      speech: isNew
        ? 'Sou a Sixy — a IA neural do Six Pass. Você ainda não usou o app, mas em 40 segundos eu te mostro como funciona e o quanto vale a pena.'
        : `Sou a Sixy. Vi que você já fez ${checkinCount} check-in${checkinCount > 1 ? 's' : ''} — bora dar uma volta e ver onde dá pra render mais?`,
      placement: 'center',
      accent: 'cyan',
      emoji: '🤖',
      sixyMood: 'happy', // se apresentando, calma e amigável
    },
    {
      // Step crítico: deixar claro que o app é EXTENSÃO do cartão físico,
      // não substitui. Aluno tem o Six Pass de plástico (Six Formaturas) e
      // esse app é a "porta digital" pros benefícios dele.
      id: 'whats-six-pass',
      title: 'Primeiro: o cartão',
      speech:
        'Six Pass é o cartão de benefícios que você ganhou da Six Formaturas — o físico, de plástico. Esse app aqui é só a extensão dele: te ajuda a achar parceiros, fazer check-in e acompanhar quanto você economiza. Não substitui o cartão.',
      placement: 'center',
      accent: 'blue',
      emoji: '💳',
      sixyMood: 'thinking',
    },
    {
      id: 'why',
      title: 'Por que isso vale?',
      speech:
        'Formando gasta em média R$ 800/mês entre comida, lazer e beleza. Com Six Pass, 15-30% disso volta — uns R$ 150 a R$ 250 todo mês. Em 12 meses: R$ 2.000 que ficou no seu bolso.',
      placement: 'center',
      accent: 'pink',
      emoji: '💡',
      sixyMood: 'thinking', // explicando dados, mood reflexivo
    },
    {
      id: 'sixy',
      title: 'Essa sou eu',
      speech:
        'Tô sempre online aqui na Home. Pergunta tipo "tem algo barato pra comer hoje?" e eu busco nos parceiros da sua cidade na hora.',
      target: 'sixy-card',
      route: '/',
      placement: 'bottom',
      accent: 'cyan',
      emoji: '💬',
      sixyMood: 'excited', // se apresentando com sparkles
    },
    {
      id: 'partners',
      title: 'Parceiros ativos',
      speech:
        'Esses são os locais com desconto Six na sua cidade agora. Toca num pra ver detalhes, regras e o que dá pra fazer lá.',
      target: 'home-partners-section',
      route: '/',
      placement: 'top',
      accent: 'pink',
      emoji: '🏪',
      sixyMood: 'happy',
    },
    {
      id: 'explore-nav',
      title: 'Catálogo completo',
      speech:
        'Em Explorar você filtra por categoria, cidade e distância. Use quando já souber o que quer.',
      target: 'nav-explore',
      placement: 'top',
      accent: 'pink',
      emoji: '🔍',
      sixyMood: 'thinking', // explorando catalog
    },
    {
      id: 'profile-nav',
      title: 'Sua área',
      speech:
        'No Perfil mora tudo seu: economia em tempo real, conquistas desbloqueadas, configurações e seu número Six Pass.',
      target: 'nav-profile',
      placement: 'top',
      accent: 'blue',
      emoji: '👤',
      sixyMood: 'happy',
    },
    {
      id: 'economy',
      title: 'ROI do seu Six Pass',
      speech:
        'Esse card cresce a cada check-in registrado. Em 4 saídas/mês na média, junta uns R$ 100. Em 6 meses: R$ 600 que voltou pro bolso.',
      target: 'profile-economy',
      route: '/perfil',
      placement: 'bottom',
      accent: 'emerald',
      emoji: '💰',
      sixyMood: 'excited', // dinheiro voltando! sparkles
    },
    {
      id: 'sixcoins',
      title: 'Six Coins',
      speech:
        'A moeda do app. Você ganha em cada check-in, em conquistas desbloqueadas e em missões. Troca por temas, molduras e benefícios exclusivos no Perfil.',
      placement: 'center',
      accent: 'amber',
      emoji: '🪙',
      sixyMood: 'excited', // moedas caindo, vibe celebratória
    },
    {
      id: 'achievements',
      title: 'Suas conquistas',
      speech:
        'Emblemas desbloqueiam automático: Foodie (10 gastro), Economista (R$ 100 poupados), Explorador (5 categorias). Cada um dá Six Coins de bônus.',
      target: 'profile-achievements',
      route: '/perfil',
      placement: 'top',
      accent: 'amber',
      emoji: '🏆',
      sixyMood: 'happy',
    },
    {
      id: 'secret',
      title: 'O macete',
      speech:
        'Parceiros "Bombando" 🔥 trocam toda semana — geralmente os novos têm o melhor desconto. Vale dar uma olhada às quintas e nos fins de semana.',
      placement: 'center',
      accent: 'violet',
      emoji: '✨',
      sixyMood: 'thinking', // segredo/insider tip — vibe conspiratória
    },
    {
      id: 'end',
      title: 'Bora começar',
      speech:
        '+50 Six Coins por completar o tour, já tá no seu perfil. Tô sempre aqui se precisar — só me chamar na Home.',
      placement: 'center',
      accent: 'violet',
      emoji: '🎁',
      sixyMood: 'excited', // celebração final
    },
  ];
}

interface GuidedTourProps {
  open: boolean;
  onClose: () => void;
  /** URL da imagem do cartão Six Pass (pra spinner 3D no step whats-six-pass). */
  cardImageUrl?: string;
}

interface TargetGeometry {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TARGET_PADDING = 10;
const TARGET_RADIUS = 18;
const BUBBLE_W = 320;
const VIEWPORT_PAD = 16;
const POINTER_H = 10;

export function GuidedTour({ open, onClose, cardImageUrl }: GuidedTourProps) {
  const [index, setIndex] = useState(0);
  const [geom, setGeom] = useState<TargetGeometry | null>(null);
  const [bubbleHeight, setBubbleHeight] = useState(200);
  // Flying state — true durante transição entre steps. Dispara arco de voo
  // (lift up + lean forward + land) por cima da animação de posição.
  const [flying, setFlying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lookupAttemptsRef = useRef(0);
  const bubbleRef = useRef<HTMLDivElement>(null);
  // Trail: refs pra prev/current position do container. Atualizadas em
  // effects ordenados: prev recebe current ANTES de re-render do novo step.
  // Inicializa em null pra detectar "primeira passagem" e NÃO renderizar
  // trail vindo de (0,0) — só renderiza trail quando já temos uma pos válida.
  const currentPosRef = useRef<{ top: number; left: number } | null>(null);
  const prevPosRef = useRef<{ top: number; left: number } | null>(null);
  const hasPrevPosRef = useRef(false);
  // isTalking: true enquanto título tá digitando — pra Sixy mexer a boca.
  const [isTalking, setIsTalking] = useState(false);

  // Steps personalizados pelo perfil do aluno (nome + check-ins).
  const { profile, updateUserProfile } = useAuth();
  const { showToast } = useToast();
  const firstName = profile?.name?.split(' ')[0] || '';
  const checkinCount = profile?.checkinCount || 0;
  const STEPS = useMemo(
    () => buildSteps({ firstName, checkinCount }),
    [firstName, checkinCount],
  );

  const step = STEPS[index];

  useEffect(() => {
    if (open) {
      setIndex(0);
      setGeom(null);
    }
  }, [open]);

  // Trava scroll do USUÁRIO mas permite scrollIntoView programático.
  // Antes usava overflow:hidden, mas isso quebrava o scrollIntoView que
  // o tour faz pra trazer cada target à vista. Agora previne só os
  // eventos de scroll do usuário (wheel, touch, keys).
  useEffect(() => {
    if (!open) return;
    const isInputTarget = (e: Event) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable;
    };
    const preventScroll = (e: Event) => {
      if (isInputTarget(e)) return;
      // Permite scroll dentro da bubble do tour (futuro: scrollable content)
      const path = (e as Event & { composedPath?: () => EventTarget[] }).composedPath?.();
      if (path?.some((n) => (n as HTMLElement)?.getAttribute?.('role') === 'dialog')) {
        return;
      }
      e.preventDefault();
    };
    const blockKeys = (e: KeyboardEvent) => {
      if (isInputTarget(e)) return;
      const blocked = [
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
        ' ',
        'Home',
        'End',
      ];
      if (blocked.includes(e.key)) e.preventDefault();
    };
    document.addEventListener('wheel', preventScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', preventScroll, {
      passive: false,
      capture: true,
    });
    document.addEventListener('keydown', blockKeys, { capture: true });
    return () => {
      document.removeEventListener('wheel', preventScroll, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener('touchmove', preventScroll, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener('keydown', blockKeys, {
        capture: true,
      } as EventListenerOptions);
    };
  }, [open]);

  // Haptic + som ao trocar de step. Sons especiais em steps específicos.
  useEffect(() => {
    if (!open) return;
    haptic('light');
    if (step.id === 'sixcoins') {
      // Chuva de moedas precisa de cha-ching, não whoosh genérico
      playSound('chaching');
    } else {
      playSound('tourStep');
    }
  }, [open, index, step.id]);

  // Sixy "fala" enquanto o título digita. ~28ms/char + 200ms buffer.
  useEffect(() => {
    if (!open) return;
    setIsTalking(true);
    const durationMs = step.title.length * 28 + 250;
    const timer = window.setTimeout(() => setIsTalking(false), durationMs);
    return () => window.clearTimeout(timer);
  }, [open, step.id, step.title]);

  // Trail tracking: prev recebe current ANTES de step mudar.
  // Cleanup do effect anterior do step roda quando step.id muda → capture
  // a posição atual antes do re-render do novo step.
  // hasPrevPosRef vira true só depois da primeira transição válida — evita
  // trail aparecer voando de (0,0) na primeira passagem.
  useEffect(() => {
    return () => {
      if (currentPosRef.current) {
        prevPosRef.current = { ...currentPosRef.current };
        hasPrevPosRef.current = true;
      }
    };
  }, [step.id]);

  // Flying trigger — quando step muda (index), dispara animação de voo
  // por 800ms. Permite que o motion.div ao redor da Sixy aplique arco
  // (lift up + lean + land) por cima do spring de posição.
  useEffect(() => {
    if (!open) return;
    setFlying(true);
    const t = window.setTimeout(() => setFlying(false), 800);
    return () => window.clearTimeout(t);
  }, [index, open]);

  // Navega pra rota necessária
  useEffect(() => {
    if (!open) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [open, step.route, location.pathname, navigate]);

  // Encontra e mede o target.
  // Usa `behavior: 'instant'` no scrollIntoView pra evitar timing race com
  // medida — smooth scroll de containers grandes leva 600-1200ms e o
  // getBoundingClientRect captura posição mid-scroll. A "feeling de voo"
  // fica por conta da animação spring do container do bubble.
  useLayoutEffect(() => {
    if (!open) return;
    if (!step.target) {
      setGeom(null);
      return;
    }
    lookupAttemptsRef.current = 0;
    let cancelled = false;

    const findAndMeasure = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
      // Retry se elemento ainda não montou OU tá com 0 dimensões (loading state)
      if (!el || el.getBoundingClientRect().height < 20) {
        lookupAttemptsRef.current += 1;
        if (lookupAttemptsRef.current < 30) {
          window.setTimeout(findAndMeasure, 200);
        } else {
          setGeom(null);
        }
        return;
      }
      const isFixedAtBottom =
        step.target?.startsWith('nav-') || step.placement === 'top';
      try {
        el.scrollIntoView({
          behavior: 'instant' as ScrollBehavior,
          block: isFixedAtBottom ? 'end' : 'center',
        });
      } catch {
        el.scrollIntoView({ block: isFixedAtBottom ? 'end' : 'center' });
      }
      // Triple rAF — garante que post-scroll layout settled antes de medir
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        window.requestAnimationFrame(() => {
          if (cancelled) return;
          window.requestAnimationFrame(() => {
            if (cancelled) return;
            const r = el.getBoundingClientRect();
            setGeom({ top: r.top, left: r.left, width: r.width, height: r.height });
          });
        });
      });
    };

    // Rotas com data fetching (perfil) precisam de mais tempo pra mount
    const initialDelay =
      step.route && location.pathname !== step.route ? 500 : 100;
    window.setTimeout(findAndMeasure, initialDelay);

    return () => {
      cancelled = true;
    };
  }, [open, step.target, step.route, location.pathname, index, step.placement]);

  // Re-mede bubble depois de cada step pra posicionar corretamente
  useLayoutEffect(() => {
    if (!bubbleRef.current) return;
    const h = bubbleRef.current.getBoundingClientRect().height;
    if (Math.abs(h - bubbleHeight) > 4) {
      setBubbleHeight(h);
    }
  }, [step.id, geom, bubbleHeight]);

  // Resize + scroll re-medem o target E forçam re-render (containerTop/Left
  // são derivados de window.innerWidth/Height inline no render — precisa
  // re-render pra refletir resize).
  // App usa scrollable interno (div overflow-y-auto), não window. CAPTURE
  // phase no document pega scroll de qualquer container ancestral.
  // Tick state força re-render mesmo em welcome (sem target).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const handle = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        // Re-mede target se houver
        if (step.target) {
          const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
          if (el) {
            const r = el.getBoundingClientRect();
            setGeom({ top: r.top, left: r.left, width: r.width, height: r.height });
          }
        }
        // Força re-render pra recalcular posições derivadas de window.*
        setTick((t) => t + 1);
      });
    };
    window.addEventListener('resize', handle);
    document.addEventListener('scroll', handle, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', handle);
      document.removeEventListener('scroll', handle, { capture: true } as EventListenerOptions);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [open, step.target]);

  // Suppress unused warning - tick is intentional re-render trigger
  void tick;

  // Teclado
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goBack();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  if (!open) return null;

  // Recompensa única por completar o tour. localStorage flag impede claim
  // duplicado mesmo se user re-disparar o tour via "Ver tutorial novamente".
  const claimTourReward = async () => {
    const STORAGE_KEY = 'sixpass.tourRewardClaimed';
    try {
      if (localStorage.getItem(STORAGE_KEY)) return; // já reclamou
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // private mode — skip claim mas continua o resto
      return;
    }
    if (!profile || !updateUserProfile) return;
    const REWARD_COINS = 50;
    try {
      await updateUserProfile({ points: (profile.points || 0) + REWARD_COINS });
      showToast(`+${REWARD_COINS} Six Coins por completar o tour!`, 'success');
      trackEvent('tour_completed', { coins_awarded: REWARD_COINS });
    } catch (err) {
      console.warn('[GuidedTour] reward claim falhou', err);
    }
  };

  const goNext = () => {
    if (index >= STEPS.length - 1) {
      // Celebração final + reward
      haptic('success');
      playSound('reward');
      void claimTourReward();
      const end = Date.now() + 1100;
      const burst = () => {
        void fireConfetti({
          particleCount: 10,
          angle: 60,
          spread: 65,
          origin: { x: 0.15, y: 0.7 },
          colors: ['#3b82f6', '#22d3ee', '#f472b6', '#a855f7', '#fbbf24'],
        });
        void fireConfetti({
          particleCount: 10,
          angle: 120,
          spread: 65,
          origin: { x: 0.85, y: 0.7 },
          colors: ['#3b82f6', '#22d3ee', '#f472b6', '#a855f7', '#fbbf24'],
        });
        if (Date.now() < end) requestAnimationFrame(burst);
      };
      burst();
      // Espera mais tempo pro user ver "Bora começar" + confetti
      setTimeout(() => onClose(), 600);
      return;
    }
    setIndex((i) => i + 1);
  };
  const goBack = () => {
    if (index === 0) return;
    setIndex((i) => i - 1);
  };

  const isFirst = index === 0;
  const isLast = index === STEPS.length - 1;
  const hasTarget = Boolean(step.target && geom);
  const isCenter = step.placement === 'center' || !hasTarget;
  const wantAbove = step.placement === 'top';
  const accent = ACCENTS[step.accent || 'blue'];

  // === Cálculo de posição do container (avatar + bubble + pointer) ===
  let containerTop = 0;
  let containerLeft = 0;

  if (isCenter) {
    containerTop = window.innerHeight / 2 - bubbleHeight / 2;
    containerLeft = window.innerWidth / 2 - BUBBLE_W / 2;
  } else if (geom) {
    const targetCenterX = geom.left + geom.width / 2;
    containerLeft = Math.max(
      VIEWPORT_PAD,
      Math.min(
        window.innerWidth - BUBBLE_W - VIEWPORT_PAD,
        targetCenterX - BUBBLE_W / 2,
      ),
    );

    if (wantAbove) {
      // Bubble acima do alvo. Pointer aponta pra baixo (em direção ao alvo).
      containerTop =
        geom.top - TARGET_PADDING - POINTER_H - bubbleHeight - 4;
    } else {
      // Bubble abaixo do alvo. Pointer aponta pra cima.
      containerTop = geom.top + geom.height + TARGET_PADDING + POINTER_H + 4;
    }

    // Garante que o container não saia da viewport (clamp vertical).
    // 70 = avatar peek (60) + folga (10) — avatar nunca corta no topo.
    containerTop = Math.max(
      VIEWPORT_PAD + 70,
      Math.min(window.innerHeight - bubbleHeight - VIEWPORT_PAD, containerTop),
    );
  }

  // Sixy "olha" pro target: lean angle calculado pela direção do avatar
  // pro spotlight. Pequena rotação (-10° a +10°) baseada em deltaX.
  let leanAngle = 0;
  if (geom && !isCenter) {
    const avatarCenterX = containerLeft + BUBBLE_W / 2;
    const targetCenterX = geom.left + geom.width / 2;
    const dx = targetCenterX - avatarCenterX;
    leanAngle = Math.max(-10, Math.min(10, dx / 30));
  }

  // Atualiza currentPosRef ao final do render (ref mutation OK aqui).
  // Cleanup do useEffect[step.id] vai copiar isso pra prevPosRef quando
  // step mudar. Trail usa prevPosRef como ponto de partida.
  currentPosRef.current = { top: containerTop, left: containerLeft };

  return (
    <AnimatePresence>
      <motion.div
        key="tour-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0"
        style={{ zIndex: Z.TOUR }}
        aria-modal="true"
        role="dialog"
        aria-label="Tour guiado pela Sixy"
      >
        {/* Backdrop tint sutil — gradient radial na cor do step */}
        {!hasTarget && (
          <div
            className="absolute inset-0 bg-black/88 backdrop-blur-md"
            style={{ backgroundImage: accent.backdrop, backgroundBlendMode: 'screen' }}
          />
        )}

        {/* Chuva de Six Coins — step dedicado de coins + step final do tour */}
        {(step.id === 'sixcoins' || step.id === 'end') && (
          <CoinShower
            count={step.id === 'end' ? 26 : 20}
            durationMs={step.id === 'end' ? 2600 : 2200}
          />
        )}

        {/* Holograma do cartão — step 'whats-six-pass'. Fica BEM ACIMA da
            bubble (top ~8%) e menor, pra não competir com o conteúdo.
            Sempre de frente (wobble parcial -8°/+8°), scanlines + glow ciano. */}
        {step.id === 'whats-six-pass' && cardImageUrl && (
          <div
            className="pointer-events-none fixed left-1/2 -translate-x-1/2 z-[205]"
            style={{ top: 'calc(var(--safe-top, 0px) + 6%)' }}
          >
            <HoloCard imageUrl={cardImageUrl} width={170} />
          </div>
        )}

        {/* Holograma da Six Coin — step 'sixcoins'. Posição igual: topo, menor.
            Coexiste com a chuva de moedas embaixo (CoinShower). */}
        {step.id === 'sixcoins' && (
          <div
            className="pointer-events-none fixed left-1/2 -translate-x-1/2 z-[205]"
            style={{ top: 'calc(var(--safe-top, 0px) + 8%)' }}
          >
            <HoloCoin size={110} />
          </div>
        )}

        {/* Spotlight ring colorido por accent + breathe pulse */}
        {hasTarget && geom && (
          <motion.div
            initial={false}
            animate={{
              top: geom.top - TARGET_PADDING,
              left: geom.left - TARGET_PADDING,
              width: geom.width + TARGET_PADDING * 2,
              height: geom.height + TARGET_PADDING * 2,
            }}
            transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
            className="absolute pointer-events-none"
            style={{
              borderRadius: TARGET_RADIUS,
              boxShadow: `0 0 0 9999px rgba(2, 2, 8, 0.86), 0 0 0 2px ${accent.ring}, 0 0 36px 8px ${accent.ringGlow}`,
            }}
          />
        )}
        {/* Breathe pulse — pequeno ring extra que pulsa */}
        {hasTarget && geom && (
          <motion.div
            initial={false}
            animate={{
              top: geom.top - TARGET_PADDING,
              left: geom.left - TARGET_PADDING,
              width: geom.width + TARGET_PADDING * 2,
              height: geom.height + TARGET_PADDING * 2,
              opacity: [0, 0.6, 0],
              scale: [1, 1.04, 1.08],
            }}
            transition={{
              top: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
              left: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
              width: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
              height: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
              opacity: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
              scale: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
            }}
            className="absolute pointer-events-none"
            style={{
              borderRadius: TARGET_RADIUS,
              border: `2px solid ${accent.ring}`,
              transformOrigin: 'center',
            }}
          />
        )}

        {/* Pular */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Pular tour"
          className="absolute right-4 z-30 inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/50 px-3 py-1.5 text-[11px] font-bold text-white backdrop-blur-sm hover:bg-black/70 transition-colors"
          style={{ top: 'max(1rem, calc(var(--safe-top, 0px) + 0.5rem))' }}
        >
          <X size={12} />
          Pular
        </button>

        {/* Indicador "tô apontando aqui" — dot pulsante na BORDA do spotlight
            no lado mais próximo do avatar. Substitui o pointer triangle que
            ficava escondido pelo avatar. */}
        {hasTarget && geom && !isCenter && (
          <motion.div
            aria-hidden="true"
            initial={false}
            animate={{
              top: wantAbove
                ? geom.top + geom.height + TARGET_PADDING - 3
                : geom.top - TARGET_PADDING - 3,
              left:
                Math.max(
                  geom.left,
                  Math.min(geom.left + geom.width, containerLeft + BUBBLE_W / 2),
                ) - 5,
            }}
            transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
            className="absolute pointer-events-none z-[25]"
            style={{ width: 10, height: 10 }}
          >
            <motion.div
              animate={{ scale: [1, 1.8, 1], opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              className={`h-full w-full rounded-full ${accent.dot} ${accent.dotGlow}`}
            />
          </motion.div>
        )}

        {/* Trail effect — 2 ghost copies fading do prev pra current
            position. Cria "speed lines" visualmente, sensação de voo.
            SÓ renderiza se hasPrevPosRef.current === true (não pulou de 0,0). */}
        {hasPrevPosRef.current && prevPosRef.current && [1, 2].map((i) => (
          <motion.div
            key={`trail-${step.id}-${i}`}
            initial={{
              top: prevPosRef.current!.top,
              left: prevPosRef.current!.left,
              opacity: 0.5 / i,
              scale: 0.85 - i * 0.08,
            }}
            animate={{
              top: containerTop,
              left: containerLeft,
              opacity: 0,
              scale: 0.7 - i * 0.08,
            }}
            transition={{
              duration: 0.7 + i * 0.1,
              ease: [0.32, 0.72, 0, 1],
              delay: i * 0.06,
            }}
            className="absolute z-[18] pointer-events-none"
            style={{ width: BUBBLE_W }}
          >
            {/* Ghost avatar simplificado — só o quadrado azul com glow */}
            <div
              className="absolute"
              style={{ top: -60, left: '50%', marginLeft: -26, width: 52, height: 52 }}
            >
              <div
                className="h-full w-full rounded-2xl border-2 border-blue-400/40 bg-gradient-to-br from-blue-900/70 via-blue-700/70 to-blue-900/70 shadow-[0_0_24px_rgba(0,162,255,0.4)] blur-[1px]"
                aria-hidden="true"
              />
            </div>
          </motion.div>
        ))}

        {/* Container Sixy + Bubble — persistente (NÃO unmount), só animate
            top/left smoothly entre steps. Spring mais ágil pra sentir voo. */}
        <motion.div
          initial={false}
          animate={{
            top: containerTop,
            left: containerLeft,
          }}
          transition={{
            type: 'spring',
            stiffness: 160,
            damping: 17,
            mass: 0.7,
          }}
          className="absolute z-20"
          style={{ width: BUBBLE_W }}
        >
          {/* Wrapper com perspective 3D — cria espaço pra rotação real */}
          <div
            className="absolute z-10"
            style={{
              top: -60,
              left: '50%',
              marginLeft: -26,
              width: 52,
              height: 52,
              perspective: '600px',
              perspectiveOrigin: '50% 50%',
            }}
          >
            {/* Ground shadow — sombra que escala com lean (ancora visual 3D) */}
            <motion.div
              aria-hidden="true"
              animate={{
                scaleX: 1 - Math.abs(leanAngle) / 60,
                opacity: 0.55 - Math.abs(leanAngle) / 40,
              }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="pointer-events-none absolute -bottom-4 left-1/2 -ml-7 h-2 w-14 rounded-full bg-black/55 blur-md"
            />

            {/* Sixy avatar — 3D rotação real (rotateY persistente no leanAngle
                + initial rotateY/X durante entrada faz o "voo planado"). */}
            <motion.div
              key={`avatar-${step.id}`}
              initial={{ rotateY: -32, rotateX: -10, scale: 0.85 }}
              animate={{
                rotateY: leanAngle * 2.4,
                rotateX: 0,
                scale: 1,
              }}
              transition={{
                type: 'spring',
                stiffness: 160,
                damping: 12,
                mass: 0.6,
                delay: 0.05,
              }}
              className="relative h-full w-full"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Emoji popup — bolha lateral, "flutua" no eixo Z (3D) */}
              {step.emoji && (
                <motion.div
                  key={`emoji-${step.id}`}
                  initial={{ scale: 0, rotate: -30, x: -10 }}
                  animate={{ scale: 1, rotate: 0, x: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 280,
                    damping: 14,
                    mass: 0.5,
                    delay: 0.3,
                  }}
                  className="absolute -right-7 -top-2 flex h-9 w-9 items-center justify-center rounded-2xl border border-white/15 bg-[#0a0f1f] shadow-[0_4px_14px_rgba(0,0,0,0.5)] text-base z-20"
                  aria-hidden="true"
                  style={{ transform: 'translateZ(18px)' }}
                >
                  {step.emoji}
                </motion.div>
              )}

              {/* Flight arc — quando flying=true (step muda), Sixy faz arco
                  voando (lift up + tilt forward + land bounce). Quando idle,
                  volta a flutuar suavemente. */}
              <motion.div
                animate={
                  flying
                    ? {
                        y: [0, -32, -18, 0],
                        rotateZ: [0, -12, 6, 0],
                        scale: [1, 0.9, 0.94, 1],
                      }
                    : {
                        y: [0, -4, 0, 3, 0],
                        rotateZ: [0, -2, 0, 2, 0],
                        scale: [1, 1, 1, 1, 1],
                      }
                }
                transition={
                  flying
                    ? {
                        duration: 0.75,
                        times: [0, 0.35, 0.7, 1],
                        ease: [0.32, 0.72, 0.2, 1],
                      }
                    : {
                        duration: 4.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }
                }
                className="relative h-full w-full"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Sixy unificada — mood vem do step (contexto: happy, thinking,
                    excited, etc.). leanAngle traduz em lookAt. */}
                <div className="relative h-full w-full">
                  <Sixy
                    form="robot"
                    size="xl"
                    mood={step.sixyMood || 'happy'}
                    lookAt={leanAngle / 10}
                    talking={isTalking}
                  />
                </div>
              </motion.div>

              {/* Sparkles emit ao aterrissar */}
              {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                <motion.div
                  key={`spark-${step.id}-${i}`}
                  aria-hidden="true"
                  initial={{ scale: 0.3, opacity: 0.95, x: 0, y: 0 }}
                  animate={{
                    scale: 1.2,
                    opacity: 0,
                    x: Math.cos((angle * Math.PI) / 180) * 38,
                    y: Math.sin((angle * Math.PI) / 180) * 38,
                  }}
                  transition={{ duration: 0.75, ease: 'easeOut', delay: 0.15 + i * 0.02 }}
                  className="pointer-events-none absolute top-1/2 left-1/2 -mt-1 -ml-1 h-2 w-2 rounded-full bg-gradient-to-br from-cyan-300 via-blue-300 to-pink-300 shadow-[0_0_8px_rgba(96,165,250,0.9)]"
                />
              ))}
            </motion.div>
          </div>

          {/* Bubble — conteúdo atualiza IMEDIATAMENTE (sem AnimatePresence
              mode="wait" que mantinha conteúdo antigo durante exit). */}
          <div
            ref={bubbleRef}
            className="liquid-glass relative rounded-3xl border border-blue-400/30 p-5 shadow-[0_24px_64px_rgba(0,162,255,0.18)]"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-3xl opacity-[0.04] bg-[linear-gradient(rgba(255,255,255,1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,1)_1px,transparent_1px)] bg-[size:12px_12px]"
            />

            <div className="relative">
              {/* Title/speech com key={step.id}: re-mount com fade enter,
                  content sempre fresco. Sem AnimatePresence (que segurava
                  conteúdo antigo). */}
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <p
                  className={`mb-1 text-[10px] font-black uppercase tracking-[0.18em] ${accent.textHighlight}`}
                >
                  Sixy · {index + 1}/{STEPS.length}
                </p>
                <h3 className="mb-1.5 text-base font-black text-white leading-tight min-h-[1.5rem]">
                  <TypingText text={step.title} />
                </h3>
                <p className="text-[13px] text-slate-300 leading-relaxed mb-4">
                  {step.speech}
                </p>
              </motion.div>

              <div className="flex items-center justify-center gap-1 mb-3">
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 rounded-full transition-ui ${
                      i === index
                        ? `w-6 ${accent.dot}`
                        : i < index
                          ? `w-1 ${accent.dot} opacity-50`
                          : 'w-1 bg-white/15'
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 transition-colors"
                    aria-label="Anterior"
                  >
                    <ChevronLeft size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={goNext}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${accent.ctaGradient} py-2.5 text-sm font-black text-white shadow-[0_8px_22px_rgba(0,162,255,0.3)]`}
                >
                  {isLast ? 'Aproveitar!' : isFirst ? 'Bora começar' : 'Próximo'}
                  {!isLast && <ChevronRight size={14} />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
